import { keyframes } from '@emotion/react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef, GridToolbar } from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loading } from '@/core/components';
import { createClient, getClients, updateClient } from '@/core/services/operator';
import type { Client } from '@/core/types';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/** 運営管理画面用グラデーション（黄→赤） */
const operatorGradient = 'linear-gradient(to right, #ffff00, #ff0000)';

export function ClientsPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ダイアログ状態
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const clientsData = await getClients();
      setClients(clientsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ダイアログを開く（新規作成）
  const handleOpenCreate = () => {
    setEditingClient(null);
    setFormName('');
    setFormDescription('');
    setFormError(null);
    setDialogOpen(true);
  };

  // ダイアログを開く（編集）
  const handleOpenEdit = useCallback((client: Client) => {
    setEditingClient(client);
    setFormName(client.name);
    setFormDescription(client.description);
    setFormError(null);
    setDialogOpen(true);
  }, []);

  // ダイアログを閉じる
  const handleClose = () => {
    setDialogOpen(false);
    setEditingClient(null);
    setFormName('');
    setFormDescription('');
    setFormError(null);
  };

  // 送信処理
  const handleSubmit = async () => {
    if (!formName.trim()) {
      setFormError('主催者名を入力してください');
      return;
    }

    setSubmitting(true);
    setFormError(null);

    try {
      if (editingClient) {
        await updateClient(editingClient.id, {
          name: formName.trim(),
          description: formDescription.trim(),
        });
      } else {
        await createClient({
          name: formName.trim(),
          description: formDescription.trim(),
        });
      }
      handleClose();
      fetchData();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <IconButton size="small" onClick={() => handleOpenEdit(params.row as Client)}>
            <EditIcon fontSize="small" />
          </IconButton>
        ),
      },
      {
        field: 'name',
        headerName: '主催者名',
        width: 200,
      },
      {
        field: 'description',
        headerName: '説明',
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'createdAt',
        headerName: '作成日',
        width: 160,
        valueGetter: (_value, row) => row.createdAt?.toDate?.()?.toLocaleString() || '-',
      },
    ],
    [handleOpenEdit]
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3, animation: `${fadeInUp} 0.6s ease-out` }}>
          {error}
        </Alert>
      )}

      {/* 統計カード */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          animation: `${fadeInUp} 0.6s ease-out`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* 統計ボックス */}
          <Box
            sx={{
              minWidth: 100,
              px: 2,
              py: 1,
              borderRadius: '8px',
              backgroundColor: 'action.hover',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              主催者数
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {clients.length}
            </Typography>
          </Box>

          {/* スペーサー */}
          <Box sx={{ flexGrow: 1 }} />

          {/* コントロール */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              sx={{
                borderRadius: '16px',
                border: '2px solid transparent',
                backgroundImage: `linear-gradient(#fff, #fff), ${operatorGradient}`,
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                color: '#1D1B20',
                '&:hover': {
                  backgroundImage: `linear-gradient(#f5f5f5, #f5f5f5), ${operatorGradient}`,
                },
              }}
            >
              更新
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{
                background: operatorGradient,
                color: '#1D1B20',
                borderRadius: '16px',
                '&:hover': {
                  background: operatorGradient,
                  filter: 'brightness(0.95)',
                },
              }}
            >
              新規作成
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* データグリッド */}
      <Paper
        sx={{
          height: 600,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          animation: `${fadeInUp} 0.6s ease-out 0.2s both`,
          '& .MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'action.hover',
            borderBottom: '2px solid',
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <DataGrid
          rows={clients}
          columns={columns}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
          disableRowSelectionOnClick
        />
      </Paper>

      {/* 作成/編集ダイアログ */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{editingClient ? '主催者編集' : '主催者作成'}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <TextField
              label="主催者名"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              fullWidth
              required
              placeholder="例: 愛媛マラソン実行委員会"
              autoFocus
            />
            <TextField
              label="説明"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="主催者の説明を入力"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleClose}
            sx={{
              borderRadius: '16px',
              border: '2px solid transparent',
              backgroundImage: `linear-gradient(#fff, #fff), ${operatorGradient}`,
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              color: '#1D1B20',
              '&:hover': {
                backgroundImage: `linear-gradient(#f5f5f5, #f5f5f5), ${operatorGradient}`,
              },
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              background: operatorGradient,
              color: '#1D1B20',
              borderRadius: '16px',
              '&:hover': {
                background: operatorGradient,
                filter: 'brightness(0.95)',
              },
              '&:disabled': {
                background: operatorGradient,
                opacity: 0.6,
              },
            }}
          >
            {submitting ? <CircularProgress size={24} color="inherit" /> : editingClient ? '更新' : '作成'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
